import React, { useState } from 'react'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import ProgressBar from '@/components/ui/ProgressBar'
import EmptyState from '@/components/ui/EmptyState'
import PesertaRow from '@/components/kegiatan/PesertaRow'
import type { KegiatanPeserta } from '@/types/pocketbase'
import type { PresensiRecord, StatusPresensi } from '@/hooks/usePresensi'

interface PesertaWithPresensi extends KegiatanPeserta {
  presensi?: PresensiRecord
}

interface PresensiListProps {
  peserta: PesertaWithPresensi[]
  statistik?: { hadir: number; alpha: number; izin: number; total: number }
  onAttendanceChange: (pesertaId: string, status: StatusPresensi) => void
  onBulkSave?: () => void
  loading?: boolean
}

export default function PresensiList({
  peserta,
  statistik,
  onAttendanceChange,
  onBulkSave,
  loading,
}: PresensiListProps) {
  const [search, setSearch] = useState('')
  const [mode, setMode] = useState<'ttd' | 'manual'>('manual')

  const filtered = peserta.filter((p) =>
    p.nama.toLowerCase().includes(search.toLowerCase()) ||
    p.nbm?.toLowerCase().includes(search.toLowerCase())
  )

  const hadirCount = peserta.filter((p) => p.presensi?.status === 'hadir').length
  const alphaCount = peserta.filter((p) => p.presensi?.status === 'alpha').length
  const izinCount = peserta.filter((p) => p.presensi?.status === 'izin').length

  return (
    <div className="space-y-3">
      {statistik && (
        <Card padding={false}>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm font-semibold">
              <span>Rekap Kehadiran</span>
              <span>{statistik.total} peserta</span>
            </div>
            <ProgressBar value={hadirCount} max={statistik.total} color="success" size="sm" />
            <div className="flex gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-xs text-text-secondary">Hadir {hadirCount}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-error" />
                <span className="text-xs text-text-secondary">Alpha {alphaCount}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-warning" />
                <span className="text-xs text-text-secondary">Izin {izinCount}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="Cari nama atau NBM..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant={mode === 'manual' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setMode('manual')}
        >
          Absen Manual
        </Button>
        <Button
          variant={mode === 'ttd' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setMode('ttd')}
        >
          Tanda Tangan
        </Button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="Belum ada peserta"
          description="Peserta yang diterima akan muncul di sini."
        />
      ) : (
        <Card padding={false}>
          <div className="divide-y divide-gray-100">
            {filtered.map((p) => (
              <PesertaRow
                key={p.id}
                peserta={p}
                presensi={p.presensi}
                kegiatanId={p.kegiatan}
                onAttendanceChange={
                  mode === 'manual'
                    ? (status) => onAttendanceChange(p.id, status)
                    : undefined
                }
              />
            ))}
          </div>
        </Card>
      )}

      {mode === 'ttd' && (
        <Card>
          <p className="text-sm text-text-secondary mb-2">
            Mode Tanda Tangan aktif. Ketuk peserta untuk membuka capture TTD.
          </p>
        </Card>
      )}

      {onBulkSave && (
        <Button block onClick={onBulkSave} loading={loading}>
          Simpan Semua
        </Button>
      )}
    </div>
  )
}
